export type FadedSquaresSide = "top" | "right" | "bottom" | "left";

export interface FadedSquaresSides {
	top?: boolean;
	right?: boolean;
	bottom?: boolean;
	left?: boolean;
}

export type FadedSquaresDropoffMode = "opacity" | "density";

export function clamp01(value: number): number {
	return Math.min(1, Math.max(0, value));
}

/**
 * Weight for a single edge: 1 at the edge, 0 at `fadeExtent` toward the
 * opposite side (and beyond). `falloff` 1 = linear; higher = stays strong
 * near the edge then drops faster.
 */
export function edgeWeight(
	distanceFromEdge: number,
	fadeExtent: number,
	falloff: number,
): number {
	if (fadeExtent <= 0) {
		return 0;
	}
	const t = 1 - clamp01(distanceFromEdge / fadeExtent);
	return t <= 0 ? 0 : t ** Math.max(falloff, 0.0001);
}

/**
 * Max contribution across enabled sides for a cell center in unit UV (0–1).
 */
export function cellEdgeContribution(
	nx: number,
	ny: number,
	sides: FadedSquaresSides,
	fadeExtent: number,
	falloff: number,
): number {
	let max = 0;
	if (sides.top) {
		max = Math.max(max, edgeWeight(ny, fadeExtent, falloff));
	}
	if (sides.bottom) {
		max = Math.max(max, edgeWeight(1 - ny, fadeExtent, falloff));
	}
	if (sides.left) {
		max = Math.max(max, edgeWeight(nx, fadeExtent, falloff));
	}
	if (sides.right) {
		max = Math.max(max, edgeWeight(1 - nx, fadeExtent, falloff));
	}
	return max;
}

/** Match blinking-squares cell counts for a given short-axis `gridSize`. */
export function cellsForSize(
	width: number,
	height: number,
	gridSize: number,
): { cols: number; rows: number } {
	const grid = Math.max(4, Math.min(200, Math.round(gridSize)));
	const aspect = width / Math.max(height, 1);
	if (aspect >= 1) {
		return { cols: Math.max(1, Math.round(grid * aspect)), rows: grid };
	}
	return {
		cols: grid,
		rows: Math.max(1, Math.round(grid / Math.max(aspect, 0.0001))),
	};
}

/** Deterministic 0–1 hash (same family as blinking-squares). */
export function hash21(x: number, y: number): number {
	let px = fract(x * 123.34);
	let py = fract(y * 456.21);
	const d = px * (px + 45.32) + py * (py + 45.32);
	px += d;
	py += d;
	return fract(px * py);
}

function fract(n: number): number {
	return n - Math.floor(n);
}

export interface PaintFadedSquaresOptions {
	cols: number;
	rows: number;
	squareSize: number;
	fadeExtent: number;
	falloff: number;
	sides: FadedSquaresSides;
	dropoffMode: FadedSquaresDropoffMode;
	minBrightness: number;
	intensity: number;
	/** 0–1 RGB channels for the square fill */
	color: readonly [number, number, number];
}

/**
 * Paints a static faded-squares field into an existing 2D context.
 * Canvas bitmap size should already match `width` × `height` (device pixels).
 */
export function paintFadedSquares(
	ctx: CanvasRenderingContext2D,
	width: number,
	height: number,
	options: PaintFadedSquaresOptions,
): void {
	const {
		cols,
		rows,
		squareSize,
		fadeExtent,
		falloff,
		sides,
		dropoffMode,
		minBrightness,
		intensity,
		color,
	} = options;

	ctx.clearRect(0, 0, width, height);

	const cellW = width / cols;
	const cellH = height / rows;
	const fill = clamp01(squareSize);
	const halfFill = fill * 0.5;
	const [cr, cg, cb] = color;
	const minB = clamp01(minBrightness);
	const intensityClamped = Math.max(0, intensity);

	for (let row = 0; row < rows; row++) {
		for (let col = 0; col < cols; col++) {
			const nx = (col + 0.5) / cols;
			const ny = (row + 0.5) / rows;
			const contribution = cellEdgeContribution(
				nx,
				ny,
				sides,
				fadeExtent,
				falloff,
			);
			if (contribution <= 0.001) {
				continue;
			}

			const gate = hash21(col + 11.7, row + 11.7);
			const brightRnd = hash21(col + 47.3, row + 47.3);

			if (dropoffMode === "density" && gate > contribution) {
				continue;
			}

			const baseBright = minB + (1 - minB) * brightRnd;
			// Per-cell alpha only — overall layer opacity is applied via CSS on the canvas.
			const alpha =
				dropoffMode === "opacity"
					? contribution * baseBright * intensityClamped
					: baseBright * intensityClamped;

			if (alpha <= 0.001) {
				continue;
			}

			const cx = (col + 0.5) * cellW;
			const cy = (row + 0.5) * cellH;
			const w = cellW * fill;
			const h = cellH * fill;
			ctx.fillStyle = `rgba(${Math.round(cr * 255)}, ${Math.round(cg * 255)}, ${Math.round(cb * 255)}, ${clamp01(alpha)})`;
			ctx.fillRect(cx - cellW * halfFill, cy - cellH * halfFill, w, h);
		}
	}
}

export function resolveFadedSquaresTone(
	variant: "light" | "dark" | "system" | "contrast",
	resolvedTheme: string | undefined,
): "light" | "dark" {
	if (variant === "light" || variant === "dark") {
		return variant;
	}
	if (variant === "contrast") {
		// Opposite of theme so squares read on blank page chrome (card/background).
		if (resolvedTheme === "dark") {
			return "light";
		}
		return "dark";
	}
	if (resolvedTheme === "dark") {
		return "dark";
	}
	if (resolvedTheme === "light") {
		return "light";
	}
	// Before theme hydration, prefer light squares (light-mode default).
	return "light";
}

export const FADED_SQUARES_TONE_RGB = {
	light: [1, 1, 1] as const,
	dark: [0, 0, 0] as const,
};
